import React, { useEffect, useState } from "react";
import {
	Button,
	Col,
	Container,
	FormGroup,
	Input,
	Label,
	Row,
	Spinner,
	Table,
} from "reactstrap";
import { toast } from "react-toastify";
import { DownloadData, PostData } from "../../../services/FetchData";

const UserActivityLog = () => {
	const [hits, setHits] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [limit] = useState(25);
	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [expanded, setExpanded] = useState(null);
	const [filters, setFilters] = useState({
		eventType: "",
		eventCategory: "",
		status: "",
		isUniversalPasswordLogin: "all",
		q: "",
		dateFrom: "",
		dateTo: "",
	});

	const load = (pageOverride) => {
		setLoading(true);
		const body = {
			page: pageOverride || page,
			limit,
			sortBy: "createdAt",
			sortOrder: "desc",
		};
		Object.entries(filters).forEach(([k, v]) => {
			if (v !== "" && v !== "all") body[k] = v;
		});
		if (filters.isUniversalPasswordLogin === "true") body.isUniversalPasswordLogin = true;
		if (filters.isUniversalPasswordLogin === "false") body.isUniversalPasswordLogin = false;

		PostData("ms1", "api/v1/admin/activity/list", body)
			.then((result) => {
				if (result?.status === "success") {
					setHits(result.data?.hits || []);
					setTotal(result.data?.total || 0);
					setPage(result.data?.page || page);
				} else {
					toast.error(result?.message || "Failed to load activity");
				}
			})
			.catch(() => toast.error("Failed to load activity"))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		load(1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onExport = async () => {
		if (exporting) return;
		const body = { ...filters, page: 1, limit: 25000 };
		Object.keys(body).forEach((k) => {
			if (body[k] === "" || body[k] === "all") delete body[k];
		});
		if (filters.isUniversalPasswordLogin === "true") body.isUniversalPasswordLogin = true;
		if (filters.isUniversalPasswordLogin === "false") body.isUniversalPasswordLogin = false;
		try {
			setExporting(true);
			const data = await DownloadData("ms1", "api/v1/admin/activity/export", body);
			const blob = new Blob([typeof data === "string" ? data : JSON.stringify(data)], {
				type: "text/csv;charset=utf-8",
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = "activity-export.csv";
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
			toast.success("Export completed successfully");
		} catch (error) {
			toast.error(error?.message || "Export failed");
		} finally {
			setExporting(false);
		}
	};

	const metaText = (metadata) => {
		try {
			return JSON.stringify(metadata || {}, null, 2);
		} catch {
			return "";
		}
	};

	return (
		<Container fluid className="py-4">
			<Row className="mb-3">
				<Col>
					<h2>User Activity Log</h2>
					<p className="text-muted mb-0">
						Retention up to 120 days. Authoritative vs observational events shown below.
					</p>
				</Col>
			</Row>
			<Row className="mb-3 g-2">
				<Col md={2}>
					<FormGroup>
						<Label>Event type</Label>
						<Input
							value={filters.eventType}
							onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}
						/>
					</FormGroup>
				</Col>
				<Col md={2}>
					<FormGroup>
						<Label>Category</Label>
						<Input
							value={filters.eventCategory}
							onChange={(e) => setFilters({ ...filters, eventCategory: e.target.value })}
						/>
					</FormGroup>
				</Col>
				<Col md={2}>
					<FormGroup>
						<Label>Status</Label>
						<Input
							type="select"
							value={filters.status}
							onChange={(e) => setFilters({ ...filters, status: e.target.value })}
						>
							<option value="">All</option>
							<option value="success">success</option>
							<option value="failure">failure</option>
							<option value="completed">completed</option>
							<option value="attempt">attempt</option>
						</Input>
					</FormGroup>
				</Col>
				<Col md={2}>
					<FormGroup>
						<Label>Universal PW</Label>
						<Input
							type="select"
							value={filters.isUniversalPasswordLogin}
							onChange={(e) =>
								setFilters({ ...filters, isUniversalPasswordLogin: e.target.value })
							}
						>
							<option value="all">All</option>
							<option value="true">Yes</option>
							<option value="false">No</option>
						</Input>
					</FormGroup>
				</Col>
				<Col md={2}>
					<FormGroup>
						<Label>Search</Label>
						<Input
							value={filters.q}
							onChange={(e) => setFilters({ ...filters, q: e.target.value })}
							placeholder="path / action"
						/>
					</FormGroup>
				</Col>
				<Col md={2} className="d-flex align-items-end gap-2 mb-3">
					<Button color="primary" onClick={() => load(1)} disabled={loading}>
						{loading ? <Spinner size="sm" /> : "Filter"}
					</Button>
					<Button color="secondary" outline onClick={onExport} disabled={exporting}>
						{exporting ? <Spinner size="sm" /> : "Export"}
					</Button>
				</Col>
			</Row>
			<Table responsive hover size="sm">
				<thead>
					<tr>
						<th>Time</th>
						<th>User</th>
						<th>Event</th>
						<th>Action</th>
						<th>Status</th>
						<th>Source</th>
						<th>Authority</th>
						<th>Country</th>
						<th>IP</th>
						<th>Flags</th>
					</tr>
				</thead>
				<tbody>
					{hits.map((h) => (
						<React.Fragment key={h.id || h.eventId}>
							<tr
								style={{ cursor: "pointer" }}
								onClick={() =>
									setExpanded(expanded === (h.id || h.eventId) ? null : h.id || h.eventId)
								}
							>
								<td>{h.createdAt}</td>
								<td>{h.userId}</td>
								<td>{h.eventType}</td>
								<td>{h.action}</td>
								<td>{h.status}</td>
								<td>{h.source}</td>
								<td>{h.authority}</td>
								<td>{h.countryCode}</td>
								<td>{h.ipAddress}</td>
								<td>
									{h.isUniversalPasswordLogin ? (
										<span className="badge bg-warning text-dark">Universal PW</span>
									) : null}
									{h.status === "failure" ? (
										<span className="badge bg-danger ms-1">Fail</span>
									) : null}
								</td>
							</tr>
							{expanded === (h.id || h.eventId) ? (
								<tr>
									<td colSpan={10}>
										<pre
											style={{
												whiteSpace: "pre-wrap",
												maxHeight: 240,
												overflow: "auto",
												background: "#f8f9fa",
												padding: 8,
											}}
										>
											{metaText({
												eventId: h.eventId,
												sessionId: h.sessionId,
												pagePath: h.pagePath,
												apiEndpoint: h.apiEndpoint,
												browser: h.browser,
												os: h.os,
												errorMessage: h.errorMessage,
												metadata: h.metadata,
												redactionApplied: h.redactionApplied,
											})}
										</pre>
									</td>
								</tr>
							) : null}
						</React.Fragment>
					))}
					{!hits.length && !loading ? (
						<tr>
							<td colSpan={10} className="text-center text-muted">
								No activity found
							</td>
						</tr>
					) : null}
				</tbody>
			</Table>
			<div className="d-flex justify-content-between align-items-center">
				<span>
					Total: {total} — Page {page}
				</span>
				<div className="d-flex gap-2">
					<Button
						size="sm"
						disabled={page <= 1 || loading}
						onClick={() => {
							const p = page - 1;
							setPage(p);
							load(p);
						}}
					>
						Prev
					</Button>
					<Button
						size="sm"
						disabled={page * limit >= total || loading}
						onClick={() => {
							const p = page + 1;
							setPage(p);
							load(p);
						}}
					>
						Next
					</Button>
				</div>
			</div>
		</Container>
	);
};

export default UserActivityLog;
